import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, Search, ChevronDown } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const COUNTRIES = [
  { name: 'United States', code: '+1', flag: '🇺🇸', iso: 'US' },
  { name: 'United Kingdom', code: '+44', flag: '🇬🇧', iso: 'GB' },
  { name: 'India', code: '+91', flag: '🇮🇳', iso: 'IN' },
  { name: 'Canada', code: '+1', flag: '🇨🇦', iso: 'CA' },
  { name: 'Australia', code: '+61', flag: '🇦🇺', iso: 'AU' },
  { name: 'UAE', code: '+971', flag: '🇦🇪', iso: 'AE' },
  { name: 'Saudi Arabia', code: '+966', flag: '🇸🇦', iso: 'SA' },
  { name: 'Qatar', code: '+974', flag: '🇶🇦', iso: 'QA' },
  { name: 'Kuwait', code: '+965', flag: '🇰🇼', iso: 'KW' },
  { name: 'Bahrain', code: '+973', flag: '🇧🇭', iso: 'BH' },
  { name: 'Oman', code: '+968', flag: '🇴🇲', iso: 'OM' },
  { name: 'Pakistan', code: '+92', flag: '🇵🇰', iso: 'PK' },
  { name: 'Bangladesh', code: '+880', flag: '🇧🇩', iso: 'BD' },
  { name: 'Germany', code: '+49', flag: '🇩🇪', iso: 'DE' },
  { name: 'France', code: '+33', flag: '🇫🇷', iso: 'FR' },
  { name: 'Spain', code: '+34', flag: '🇪🇸', iso: 'ES' },
  { name: 'Italy', code: '+39', flag: '🇮🇹', iso: 'IT' },
  { name: 'Netherlands', code: '+31', flag: '🇳🇱', iso: 'NL' },
  { name: 'Singapore', code: '+65', flag: '🇸🇬', iso: 'SG' },
  { name: 'Malaysia', code: '+60', flag: '🇲🇾', iso: 'MY' },
  { name: 'Nigeria', code: '+234', flag: '🇳🇬', iso: 'NG' },
  { name: 'South Africa', code: '+27', flag: '🇿🇦', iso: 'ZA' },
  { name: 'Kenya', code: '+254', flag: '🇰🇪', iso: 'KE' },
  { name: 'Ghana', code: '+233', flag: '🇬🇭', iso: 'GH' },
  { name: 'Brazil', code: '+55', flag: '🇧🇷', iso: 'BR' },
  { name: 'Mexico', code: '+52', flag: '🇲🇽', iso: 'MX' },
  { name: 'New Zealand', code: '+64', flag: '🇳🇿', iso: 'NZ' },
  { name: 'Ireland', code: '+353', flag: '🇮🇪', iso: 'IE' },
  { name: 'Sweden', code: '+46', flag: '🇸🇪', iso: 'SE' },
  { name: 'Norway', code: '+47', flag: '🇳🇴', iso: 'NO' },
  { name: 'Denmark', code: '+45', flag: '🇩🇰', iso: 'DK' },
  { name: 'Finland', code: '+358', flag: '🇫🇮', iso: 'FI' },
  { name: 'Switzerland', code: '+41', flag: '🇨🇭', iso: 'CH' },
  { name: 'Turkey', code: '+90', flag: '🇹🇷', iso: 'TR' },
  { name: 'Egypt', code: '+20', flag: '🇪🇬', iso: 'EG' },
  { name: 'Jordan', code: '+962', flag: '🇯🇴', iso: 'JO' },
  { name: 'Lebanon', code: '+961', flag: '🇱🇧', iso: 'LB' },
  { name: 'Philippines', code: '+63', flag: '🇵🇭', iso: 'PH' },
  { name: 'Indonesia', code: '+62', flag: '🇮🇩', iso: 'ID' },
  { name: 'Japan', code: '+81', flag: '🇯🇵', iso: 'JP' },
  { name: 'South Korea', code: '+82', flag: '🇰🇷', iso: 'KR' },
  { name: 'China', code: '+86', flag: '🇨🇳', iso: 'CN' },
  { name: 'Hong Kong', code: '+852', flag: '🇭🇰', iso: 'HK' },
  { name: 'Thailand', code: '+66', flag: '🇹🇭', iso: 'TH' },
  { name: 'Vietnam', code: '+84', flag: '🇻🇳', iso: 'VN' },
];

function CountryPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = COUNTRIES.find(c => c.code === value) || COUNTRIES[0];
  const filtered = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.includes(search)
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 14px', width: '100%',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer', color: 'var(--text-primary)',
          fontSize: 14,
        }}
      >
        <span style={{ fontSize: 20 }}>{selected.flag}</span>
        <span style={{ flex: 1, textAlign: 'left' }}>{selected.name}</span>
        <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 13 }}>{selected.code}</span>
        <ChevronDown size={14} color="var(--text-muted)" />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          maxHeight: 280, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-primary)', borderRadius: 6 }}>
              <Search size={13} color="var(--text-muted)" />
              <input
                ref={inputRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search country..."
                style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--text-primary)', flex: 1, outline: 'none' }}
              />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(country => (
              <button
                key={`${country.iso}-${country.code}`}
                type="button"
                onClick={() => { onChange(country.code); setOpen(false); setSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', width: '100%',
                  background: country.code === value ? 'var(--bg-hover)' : 'transparent',
                  border: 'none', cursor: 'pointer', color: 'var(--text-primary)',
                  fontSize: 13, textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 18, width: 24 }}>{country.flag}</span>
                <span style={{ flex: 1 }}>{country.name}</span>
                <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 12 }}>{country.code}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No country found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Phone format masks per country code
const PHONE_FORMATS = {
  '+1':   { mask: '(###) ###-####', placeholder: '(555) 555-5555', digits: 10 },
  '+44':  { mask: '#### ### ####', placeholder: '7911 123456', digits: 10 },
  '+91':  { mask: '##### #####', placeholder: '98765 43210', digits: 10 },
  '+61':  { mask: '### ### ###', placeholder: '412 345 678', digits: 9 },
  '+971': { mask: '## ### ####', placeholder: '50 123 4567', digits: 9 },
  '+966': { mask: '## ### ####', placeholder: '50 123 4567', digits: 9 },
  '+92':  { mask: '### #######', placeholder: '300 1234567', digits: 10 },
  '+49':  { mask: '#### #######', placeholder: '0151 1234567', digits: 11 },
  '+33':  { mask: '# ## ## ## ##', placeholder: '6 12 34 56 78', digits: 9 },
  '+34':  { mask: '### ### ###', placeholder: '612 345 678', digits: 9 },
  '+39':  { mask: '### #######', placeholder: '312 3456789', digits: 10 },
  '+55':  { mask: '(##) #####-####', placeholder: '(11) 91234-5678', digits: 11 },
  '+52':  { mask: '## #### ####', placeholder: '55 1234 5678', digits: 10 },
  '+65':  { mask: '#### ####', placeholder: '8123 4567', digits: 8 },
  '+60':  { mask: '##-#### ####', placeholder: '12-3456 7890', digits: 10 },
  '+27':  { mask: '## ### ####', placeholder: '71 234 5678', digits: 9 },
  '+234': { mask: '### ### ####', placeholder: '801 234 5678', digits: 10 },
  '+254': { mask: '### ######', placeholder: '712 345678', digits: 9 },
  '+81':  { mask: '###-####-####', placeholder: '090-1234-5678', digits: 11 },
  '+82':  { mask: '###-####-####', placeholder: '010-1234-5678', digits: 11 },
  '+86':  { mask: '### #### ####', placeholder: '138 1234 5678', digits: 11 },
};

function formatPhoneNumber(value, countryCode) {
  const format = PHONE_FORMATS[countryCode];
  if (!format) return value;

  const digits = value.replace(/\D/g, '');
  let formatted = '';
  let digitIndex = 0;

  for (let i = 0; i < format.mask.length && digitIndex < digits.length; i++) {
    if (format.mask[i] === '#') {
      formatted += digits[digitIndex++];
    } else {
      formatted += format.mask[i];
    }
  }
  return formatted;
}

export default function StepAccount({ data, updateData, onNext }) {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [localNumber, setLocalNumber] = useState('');
  const [displayNumber, setDisplayNumber] = useState('');

  // Sync phone from country code + local number
  useEffect(() => {
    if (localNumber) {
      const cleaned = localNumber.replace(/\D/g, '');
      updateData({ phone: `${countryCode}${cleaned}` });
    }
  }, [countryCode, localNumber]);

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value, countryCode);
    setDisplayNumber(formatted);
    setLocalNumber(formatted);
    setErrors(p => ({ ...p, phone: '' }));
  };

  const handleCountryChange = (code) => {
    setCountryCode(code);
    setDisplayNumber('');
    setLocalNumber('');
    setErrors(p => ({ ...p, phone: '' }));
  };

  const checkEmailAvailability = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setCheckingEmail(true);
    try {
      const res = await fetch(`${API_BASE}/auth/check-email?email=${encodeURIComponent(email)}`);
      const result = await res.json();
      if (!result.available) {
        setErrors(p => ({ ...p, email: 'This email is already registered. Sign in instead.' }));
      }
    } catch (e) {}
    setCheckingEmail(false);
  };

  const validate = () => {
    const e = {};
    if (!data.name.trim()) e.name = 'Full name is required';
    if (!data.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) e.email = 'Enter a valid email address';
    if (!localNumber.trim()) e.phone = 'Phone number is required';
    else if (localNumber.replace(/\D/g, '').length < 6) e.phone = 'Enter a valid phone number';
    if (!data.password.trim()) e.password = 'Password is required';
    else if (data.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (!data.smsConsent) e.smsConsent = 'Please agree to receive SMS notifications to continue';
    return e;
  };

  const handleNext = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setLoading(true);
    await new Promise(r => setTimeout(r, 400));
    setLoading(false);
    onNext();
  };

  return (
    <div className="ob-card">
      <h1 className="ob-title">Create your account</h1>
      <p className="ob-subtitle">Start your 14-day free trial. No credit card required.</p>

      {/* Name */}
      <div className="ob-field">
        <label className="ob-label">Full Name</label>
        <input
          className={`ob-input ${errors.name ? 'error' : ''}`}
          type="text"
          placeholder="Your full name"
          value={data.name}
          onChange={e => { updateData({ name: e.target.value }); setErrors(p => ({ ...p, name: '' })); }}
          autoFocus
        />
        {errors.name && <p className="ob-error">{errors.name}</p>}
      </div>

      {/* Email */}
      <div className="ob-field">
        <label className="ob-label">Email Address</label>
        <input
          className={`ob-input ${errors.email ? 'error' : ''}`}
          type="email"
          placeholder="you@yourbusiness.com"
          value={data.email}
          onChange={e => { updateData({ email: e.target.value }); setErrors(p => ({ ...p, email: '' })); }}
          onBlur={e => checkEmailAvailability(e.target.value)}
        />
        {checkingEmail && <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Checking availability...</p>}
        {errors.email && <p className="ob-error">{errors.email}</p>}
      </div>

      {/* Phone with country picker */}
      <div className="ob-field">
        <label className="ob-label">Your Business Phone Number</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ width: 200, flexShrink: 0 }}>
            <CountryPicker
              value={countryCode}
              onChange={handleCountryChange}
            />
          </div>
          <input
            className={`ob-input ${errors.phone ? 'error' : ''}`}
            type="tel"
            placeholder={PHONE_FORMATS[countryCode]?.placeholder || 'Phone number'}
            value={displayNumber}
            onChange={e => handlePhoneChange(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          The number your customers call. Works with any mobile or landline.
        </p>
        {errors.phone && <p className="ob-error">{errors.phone}</p>}
      </div>

      {/* Password */}
      <div className="ob-field">
        <label className="ob-label">Password</label>
        <div style={{ position: 'relative' }}>
          <input
            className={`ob-input ${errors.password ? 'error' : ''}`}
            type={showPassword ? 'text' : 'password'}
            placeholder="Minimum 8 characters"
            value={data.password}
            onChange={e => { updateData({ password: e.target.value }); setErrors(p => ({ ...p, password: '' })); }}
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute', right: 12, top: '50%',
              transform: 'translateY(-50%)',
              background: 'none', border: 'none',
              cursor: 'pointer', color: 'var(--text-muted)'
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="ob-error">{errors.password}</p>}
      </div>

      {/* SMS Consent */}
      <div className="ob-field">
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5
        }}>
          <input
            type="checkbox"
            checked={data.smsConsent || false}
            onChange={e => updateData({ smsConsent: e.target.checked })}
            style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }}
          />
          <span>
            I agree to receive SMS notifications about my business calls from Aizaan.
            Message frequency varies. Message & data rates may apply.
            Reply STOP to unsubscribe at any time.{' '}
            <a href="https://aizaan.app/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue-light)' }}>Privacy Policy</a>
          </span>
        </label>
        {errors.smsConsent && <p className="ob-error" style={{ marginTop: 6 }}>{errors.smsConsent}</p>}
      </div>

      <button className="ob-btn ob-btn-primary" onClick={handleNext} disabled={loading}>
        {loading ? 'Checking...' : 'Continue →'}
      </button>

      <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 16 }}>
        Already have an account?{' '}
        <a href="/login" style={{ color: 'var(--blue-light)' }}>Sign in</a>
      </p>
    </div>
  );
}
